// Pure Rolodex Mutation module.
//
// Views express entry/facet intent here. The Firebase adapter is responsible
// only for executing the returned state transition transactionally.

import {
  CONTEXT_PERSONAL,
  CONTEXT_PROFESSIONAL,
  discoveryEdgeEligible,
  isDiscoveryEnabled,
  normalizeOrigin as normalizeEntryOrigin,
  normalizeRolodexFacet as normalizeCanonicalFacet,
} from "./rolodexContext";
import { normalizePrivateContactProfile } from "./privateContactProfile";
import {
  CONNECTION_DETAIL_KEYS,
  normalizeConnectionDetails,
} from "./connectionDetails";

export const ROLODEX_CONTEXTS = Object.freeze([
  CONTEXT_PROFESSIONAL,
  CONTEXT_PERSONAL,
]);

const SHARED_FIELDS = Object.freeze([
  "recipient_uid",
  "recipient_name",
  "recipient_phone",
  "private_profile",
  "status",
  ...CONNECTION_DETAIL_KEYS,
]);

export { SHARED_FIELDS };

function assertContext(context) {
  if (!ROLODEX_CONTEXTS.includes(context)) {
    throw new Error(`Unknown rolodex context: ${context}`);
  }
}

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value || {}, key);
}

// Firebase rejects undefined values. Preserve Dates, Timestamps, and other SDK
// value objects while cleaning ordinary objects supplied by forms.
export function withoutUndefined(value) {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) {
    return value.map(withoutUndefined).filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return value;
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, item]) => [key, withoutUndefined(item)])
        .filter(([, item]) => item !== undefined)
    );
  }
  return value;
}

function persistedOrigin(origin) {
  const normalized = normalizeEntryOrigin(origin);
  if (!normalized?.kind) return null;

  if (normalized.kind === "entry" && normalized.entry_id) {
    return withoutUndefined({
      kind: "entry",
      entry_id: String(normalized.entry_id),
      name: normalized.name || normalized.parent_name || "",
      mode: normalized.mode === "sub" ? "sub" : "direct",
    });
  }

  if (normalized.kind === "user" && normalized.user_uid) {
    return withoutUndefined({
      kind: "user",
      user_uid: String(normalized.user_uid),
      name: normalized.name || normalized.parent_name || "",
      mode: normalized.mode === "sub" ? "sub" : "direct",
    });
  }

  return null;
}

/** Professional discovery defaults on; Personal requires explicit opt-in. */
export function defaultDiscoveryFor(context) {
  assertContext(context);
  return isDiscoveryEnabled({}, context);
}

/** Merge a partial Facet intent with stored values and canonical defaults. */
export function normalizeRolodexFacet(context, facet, existingFacet = null) {
  assertContext(context);
  if (facet === null) return null;

  const incoming = facet || {};
  const existing = existingFacet || {};
  const merged = withoutUndefined({ ...existing, ...incoming });
  const canonical = normalizeCanonicalFacet(merged, context);
  const stored = { ...canonical };
  delete stored.context;
  return withoutUndefined({
    ...stored,
    origin: persistedOrigin(canonical.origin),
  });
}

/** True when a registered contact publishes one minimal Discovery Edge. */
export function shouldPublishDiscoveryEdge(entry) {
  return discoveryEdgeEligible(entry);
}

/** Build the complete next entry from a partial mutation intent. */
export function prepareRolodexEntry(patch, existing = null) {
  const next = withoutUndefined({ ...(existing || {}) });
  delete next.id;
  delete next.otherProfile;
  delete next.updated_at;

  SHARED_FIELDS.forEach((field) => {
    if (hasOwn(patch, field)) next[field] = withoutUndefined(patch[field]);
  });

  next.recipient_uid = next.recipient_uid || null;
  next.recipient_name =
    typeof next.recipient_name === "string" ? next.recipient_name.trim() : "";
  next.recipient_phone =
    typeof next.recipient_phone === "string" ? next.recipient_phone.trim() : "";
  const privateProfile = normalizePrivateContactProfile(next.private_profile);
  if (Object.keys(privateProfile).length) next.private_profile = privateProfile;
  else delete next.private_profile;
  next.status = next.status || "confirmed";

  // Rich detail is entry-level, shared across both facets. Empty values are
  // dropped rather than stored blank, matching private_profile.
  const details = normalizeConnectionDetails(next);
  CONNECTION_DETAIL_KEYS.forEach((key) => delete next[key]);
  Object.assign(next, details);

  const storedFacets = existing?.facets || {};
  const incomingFacets = patch?.facets || {};
  const facets = {};

  ROLODEX_CONTEXTS.forEach((context) => {
    if (hasOwn(incomingFacets, context)) {
      const normalized = normalizeRolodexFacet(
        context,
        incomingFacets[context],
        storedFacets[context]
      );
      if (normalized) facets[context] = normalized;
    } else if (storedFacets[context]) {
      facets[context] = normalizeRolodexFacet(
        context,
        storedFacets[context],
        storedFacets[context]
      );
    }
  });
  next.facets = facets;

  if (!next.recipient_uid && !next.recipient_name) {
    throw new Error("A registered user or a name is required for a rolodex entry.");
  }

  return next;
}

/** Publication/index changes for one atomic entry transition. */
export function rolodexPublicationPlan(entryId, previous, next) {
  const oldTarget = previous?.recipient_uid || null;
  const newTarget = next?.recipient_uid || null;
  const newPublished = shouldPublishDiscoveryEdge(next);
  const removeEdgeTargets = [...new Set([
    oldTarget && (oldTarget !== newTarget || !newPublished) ? oldTarget : null,
    newTarget && !newPublished ? newTarget : null,
  ].filter(Boolean))];

  return {
    entryId,
    oldTarget,
    newTarget,
    removeOldIndex: Boolean(oldTarget && oldTarget !== newTarget),
    claimNewIndex: Boolean(newTarget),
    // Delete stale edges even when legacy entry metadata says they should not
    // have existed; the transaction repairs publication drift on every write.
    removeOldEdge: removeEdgeTargets.length > 0,
    removeEdgeTargets,
    publishNewEdge: Boolean(newTarget && newPublished),
  };
}

export function duplicateRecipientError(entryId) {
  const error = new Error("This registered user is already in your rolodex.");
  error.code = "rolodex/duplicate-recipient";
  error.entryId = entryId;
  return error;
}
